/**
 * FE-4577 — merge the store into a payload, then substitute tokens.
 *
 * ORDER IS THE WHOLE POINT. Before this ticket `normalizeAppData` substituted
 * `{{_s.*}}`/`{{_c.*}}` into class and template bodies as they arrived, and the
 * substituted copies were all that survived. That cannot be persisted: the
 * backend never re-sends a class it believes the client holds, so a body with
 * one theme's values baked in would render stale for ever after a theme switch.
 * The store therefore holds RAW bodies and substitution happens HERE, against
 * whatever theme is current, every time a payload is normalized.
 *
 * That would be ruinously expensive done naively — after warm-up the merged map
 * is the branch's ENTIRE class set on every single response — so it is memoised
 * on two levels:
 *
 *   level 2  source map  -> resolved map   (identity in, identity out)
 *   level 1  source body -> resolved body  (a delivery of 3 classes into a map
 *                                           of 400 substitutes 3)
 *
 * Both hang off a single-entry scope cache: the scope changes only when the
 * store's content revision or the active theme changes, NOT when the delivery
 * mark moves — and the mark moves on every response.
 */

import { resolveThemeVariables, substituteTokensDeep, TokenScope } from '../designTokens';
import { isDesignSystemActive, readDesignSystem } from './store';

type AnyRecord = Record<string, any>;

type ScopeCache = {
  key: string;
  scope: TokenScope;
  themeVariables: Record<string, unknown>;
  hasTokens: boolean;
  /** level 2 — source map identity to resolved map identity */
  maps: WeakMap<object, AnyRecord>;
  /** level 1 — source body identity to resolved body identity */
  bodies: WeakMap<object, any>;
};

export type DeliveredBlocks = {
  classes?: AnyRecord;
  templates?: AnyRecord;
  styles?: Record<string, unknown>;
  configTokens?: Record<string, unknown>;
};

export type ResolvedDesignSystem = {
  hasTokens: boolean;
  themeVariables: Record<string, unknown>;
  scope: TokenScope;
  classes?: AnyRecord;
  templates?: AnyRecord;
};

const isPlainObject = (value: unknown): value is AnyRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isEmpty = (value: AnyRecord | undefined): boolean =>
  !value || Object.keys(value).length === 0;

let scopeCache: ScopeCache | null = null;

/**
 * Store wins over what the payload carried.
 *
 * For a NETWORK payload the two agree — ingestion ran first, so everything
 * delivered is already in the store. For a CACHE read they do not: the entry may
 * embed blocks written by an older build, and the store is fresher.
 *
 * When nothing was delivered — the common case once warm — the store's own map
 * object is returned by identity, which is what lets the memo below hit.
 */
const mergeMaps = (delivered: AnyRecord | undefined, stored: AnyRecord): AnyRecord => {
  if (isEmpty(delivered)) return stored;
  if (isEmpty(stored)) return delivered as AnyRecord;
  return { ...delivered, ...stored };
};

const substituteMap = (source: AnyRecord, cache: ScopeCache): AnyRecord => {
  const hit = cache.maps.get(source);
  if (hit) return hit;

  let changed = false;
  const out: AnyRecord = {};
  Object.entries(source).forEach(([id, body]) => {
    let resolved: any;
    if (body && typeof body === 'object') {
      const bodyHit = cache.bodies.get(body);
      if (bodyHit !== undefined) {
        resolved = bodyHit;
      } else {
        resolved = substituteTokensDeep(body, cache.scope);
        cache.bodies.set(body, resolved);
      }
    } else {
      resolved = substituteTokensDeep(body, cache.scope);
    }
    if (resolved !== body) changed = true;
    out[id] = resolved;
  });

  // Unchanged ⇒ hand back the source itself, so identity survives the pass.
  const result = changed ? out : source;
  cache.maps.set(source, result);
  return result;
};

/**
 * Builds the effective view of the design system for one payload: the store
 * merged with whatever this payload delivered, with tokens resolved.
 */
export const resolveDeliveredDesignSystem = (delivered: DeliveredBlocks): ResolvedDesignSystem => {
  const state = readDesignSystem();

  // The theme block this payload should paint with. A response that carries its
  // own `styles` wins; otherwise the named theme the store holds; otherwise the
  // unnamed one (styles that arrived with no `screen.settings.theme` to key on).
  const storedTheme = (state.activeTheme && state.themes[state.activeTheme])
    || state.unnamedTheme
    || undefined;
  const effectiveStyles = isPlainObject(delivered.styles) ? delivered.styles : storedTheme;
  const effectiveConfig = isPlainObject(delivered.configTokens)
    ? delivered.configTokens
    : (state.configTokens ?? undefined);

  // Keyed on what actually determines the scope. The delivery mark is
  // deliberately absent: it changes on every response and would defeat the memo.
  const key = [
    state.contentRevision,
    state.activeTheme ?? '',
    isPlainObject(delivered.styles) ? 'ds' : '',
    isPlainObject(delivered.configTokens) ? 'dc' : '',
  ].join('|');

  if (!scopeCache || scopeCache.key !== key) {
    const themeVariables = resolveThemeVariables(effectiveStyles, effectiveConfig);
    scopeCache = {
      key,
      themeVariables,
      scope: { styles: themeVariables, config: effectiveConfig },
      // FE-4577 finding B: computed from the EFFECTIVE scope, not the payload.
      // Once the backend stops sending `styles`/`config` every payload declares
      // no tokens of its own, and the old payload-only test would leave every
      // `{{_s.accent}}` rendering as literal text.
      hasTokens: Object.keys(themeVariables).length > 0 || !!effectiveConfig,
      maps: new WeakMap(),
      bodies: new WeakMap(),
    };
  }
  const cache = scopeCache;

  const finish = (merged: AnyRecord): AnyRecord | undefined => {
    if (isEmpty(merged)) return undefined;
    return cache.hasTokens ? substituteMap(merged, cache) : merged;
  };

  return {
    hasTokens: cache.hasTokens,
    themeVariables: cache.themeVariables,
    scope: cache.scope,
    classes: finish(mergeMaps(delivered.classes, state.classes)),
    templates: finish(mergeMaps(delivered.templates, state.templates)),
  };
};

/** Test/diagnostic hook — drops the memo so the next call rebuilds. */
export const clearDesignSystemResolveCache = (): void => {
  scopeCache = null;
};

/**
 * FE-4577 — strip what the design-system store owns before a body is written to
 * the screen cache (`cacheScreens`).
 *
 * The trust boundary now merges the store into EVERY body, so the naive
 * `setLocalCache(key, { ...data, ... })` would embed the branch's entire class
 * and template set into every cached screen — duplicated N times and able to go
 * stale independently of the store. Cached entries carry no design system at
 * all; the boundary re-merges the live store when the entry is read back, which
 * is also why an offline replay still renders styled.
 *
 * `session_data.fds` goes too: a replayed mark would claim a delivery this
 * client may never have applied.
 */
export const toCacheableBody = (body: any): any => {
  if (!isPlainObject(body)) return body;

  // Only discard what the store can hand back. Against a backend that does not
  // speak FE-4576 the store stays permanently empty, so stripping here would
  // cache screens whose classes and templates nothing can restore and they would
  // replay unstyled. Confirmed by test, and it is the exact failure a renderer
  // deployed ahead of the engine would ship.
  //
  // Entries written before the protocol goes live stay whole and keep working:
  // the read-side merge is `{ ...embedded, ...store }`, so the store wins where
  // it has an id and the embedded copy fills the rest.
  if (!isDesignSystemActive()) return body;

  const { classes, templates, styles, ...rest } = body;

  if (isPlainObject(rest.config)) {
    const { classes: cfgClasses, templates: cfgTemplates, ...restConfig } = rest.config;
    rest.config = restConfig;
  }

  if (isPlainObject(rest.session_data) && 'fds' in rest.session_data) {
    const { fds, ...restSession } = rest.session_data;
    rest.session_data = restSession;
  }

  return rest;
};
